/**
 * XYZ 3D Lissajous Visualizer
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { AudioParser, WaveData } from './WaveformVisualizer';

export class XYZVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'xyz',
    name: '3D Lissajous',
    description: '3D phase correlation visualization',
    category: '3d',
    defaultZoom: 0.8,
    supports3D: true,
    supportsAutoRotation: true,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): number | void {
    const { 
      ctx, width, height, activeWaves, speed, time, deltaTime, 
      zoom, pan, isAutoRotating, autoRotRef = 0, uploadedAudioData, isUploadedAudioPlaying
    } = config;
    const cx = width / 2;
    const cy = height / 2;

    // Clear canvas
    ctx.fillStyle = '#0b0f19';
    ctx.fillRect(0, 0, width, height);

    // When music is playing, use AudioParser for cleaner peak detection
    let displayWaves = activeWaves;
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      const parsedData = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
      displayWaves = parsedData.map((w, index) => ({
        ...w,
        id: index,
        waveform: w.waveform as any,
        muted: false
      }));
    }

    if (displayWaves.length === 0) return autoRotRef;

    // Camera setup
    const cameraZ = 1200 / Math.max(0.1, zoom);
    const scale = Math.min(width, height) * 0.4;

    // Auto rotation
    let newAutoRot = autoRotRef;
    if (isAutoRotating) {
      newAutoRot += deltaTime * 0.2;
    }

    const rotX = (pan.y * 0.01) + 0.5;
    const rotY = (pan.x * 0.01) + newAutoRot;

    // Draw 3D bounding box
    this.drawBoundingBox(ctx, scale, rotX, rotY, cameraZ, cx, cy);

    const points = 800;
    const cycles = 4;

    ctx.lineWidth = 4;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowBlur = isUploadedAudioPlaying ? 0 : 15;

    // Separate waves by channel
    const pureLeftWaves = displayWaves.filter(w => w.channel === 'left');
    const pureRightWaves = displayWaves.filter(w => w.channel === 'right');
    const bothWaves = displayWaves.filter(w => (w.channel || 'both') === 'both' && !((w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0));
    const autoPanWaves = displayWaves.filter(w => (w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0);
    const hasDistinctChannels = pureLeftWaves.length > 0 || pureRightWaves.length > 0;

    if (hasDistinctChannels || autoPanWaves.length > 0) {
      // STEREO MODE
      if (pureLeftWaves.length > 0) {
        this.draw3DTrace(ctx, pureLeftWaves, '#3b82f6', points, cycles, time, speed, scale, rotX, rotY, cameraZ, cx, cy, isUploadedAudioPlaying);
      }
      if (pureRightWaves.length > 0) {
        this.draw3DTrace(ctx, pureRightWaves, '#ec4899', points, cycles, time, speed, scale, rotX, rotY, cameraZ, cx, cy, isUploadedAudioPlaying);
      }
      if (bothWaves.length > 0) {
        this.draw3DTrace(ctx, bothWaves, '#10B981', points, cycles, time, speed, scale, rotX, rotY, cameraZ, cx, cy, isUploadedAudioPlaying);
      }
      if (autoPanWaves.length > 0) {
        const panColor = this.getPanColor(0);
        this.draw3DTrace(ctx, autoPanWaves, panColor, points, cycles, time, speed, scale, rotX, rotY, cameraZ, cx, cy, isUploadedAudioPlaying);
      }
    } else {
      // MONO MODE
      this.draw3DTrace(ctx, displayWaves, '#10B981', points, cycles, time, speed, scale, rotX, rotY, cameraZ, cx, cy, isUploadedAudioPlaying);
    }

    ctx.shadowBlur = 0;
    return newAutoRot;
  }

  private drawBoundingBox(
    ctx: CanvasRenderingContext2D,
    boxSize: number,
    rotX: number,
    rotY: number,
    cameraZ: number,
    cx: number,
    cy: number
  ): void {
    const size = boxSize * 1.2;
    const boxPoints = [
      {x:-1,y:-1,z:-1}, {x:1,y:-1,z:-1}, {x:1,y:1,z:-1}, {x:-1,y:1,z:-1},
      {x:-1,y:-1,z:1}, {x:1,y:-1,z:1}, {x:1,y:1,z:1}, {x:-1,y:1,z:1}
    ];

    const edges = [
      [0,1], [1,2], [2,3], [3,0],
      [4,5], [5,6], [6,7], [7,4],
      [0,4], [1,5], [2,6], [3,7]
    ];

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.beginPath();

    edges.forEach(([s, e]) => {
      const start = boxPoints[s];
      const end = boxPoints[e];
      const p1 = this.project3D(start.x * size, start.y * size, start.z * size, rotX, rotY, cameraZ, cx, cy);
      const p2 = this.project3D(end.x * size, end.y * size, end.z * size, rotX, rotY, cameraZ, cx, cy);
      if (p1 && p2) {
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
      }
    });

    ctx.stroke();
  }

  private draw3DTrace(
    ctx: CanvasRenderingContext2D,
    waves: any[],
    color: string,
    points: number,
    cycles: number,
    time: number,
    speed: number,
    scale: number,
    rotX: number,
    rotY: number,
    cameraZ: number,
    cx: number,
    cy: number,
    isMusic?: boolean
  ): void {
    if (waves.length === 0) return;

    ctx.strokeStyle = color;
    ctx.shadowColor = color;
    ctx.beginPath();

    let prevP: { x: number; y: number } | null = null;

    for (let i = 0; i < points; i++) {
      const tOffset = (i / points) * (Math.PI * 2 * cycles);
      const t = tOffset + (time * 0.5);

      const x = this.computeWaveValue(waves, t, speed);
      const y = this.computeWaveValue(waves, t + Math.PI / 2, speed);
      const z = this.computeWaveValue(waves, t + Math.PI, speed);

      const p = this.project3D(x * scale, y * scale, z * scale, rotX, rotY, cameraZ, cx, cy);
      if (p) {
        if (i === 0) {
          ctx.moveTo(p.x, p.y);
        } else if (isMusic && prevP && i > 1) {
          // Only use curves if points are close enough to avoid artifacts
          const dist = Math.sqrt((p.x - prevP.x) ** 2 + (p.y - prevP.y) ** 2);
          if (dist < 50) {
            const cpX = (prevP.x + p.x) / 2;
            const cpY = (prevP.y + p.y) / 2;
            ctx.quadraticCurveTo(prevP.x, prevP.y, cpX, cpY);
          } else {
            ctx.lineTo(p.x, p.y);
          }
        } else {
          ctx.lineTo(p.x, p.y);
        }
        prevP = p;
      }
    }

    ctx.stroke();
  }

  private computeWaveValue(waves: any[], t: number, speed: number): number {
    if (waves.length === 0) return 0;
    let sum = 0;
    let totalAmp = 0;
    for (const w of waves) {
      const phase = (w.phase * Math.PI) / 180;
      const effectiveFreq = w.freq * speed;
      sum += w.amp * Math.sin(effectiveFreq * t * 0.2 + phase);
      totalAmp += w.amp;
    }
    return totalAmp > 0 ? sum / totalAmp : 0;
  }
}
